import { useState, useEffect, useRef, RefObject } from 'react'

interface UseScrollToBottomOptions {
  threshold?: number
  onNewMessage?: () => void
}

export function useScrollToBottom(
  scrollRef: RefObject<HTMLDivElement>,
  options: UseScrollToBottomOptions = {}
) {
  const { threshold = 100, onNewMessage } = options
  const [showButton, setShowButton] = useState(false)
  const [isAtBottom, setIsAtBottom] = useState(true)
  const prevScrollHeight = useRef(0)

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    if (!scrollRef.current) return
    
    scrollRef.current.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior,
    })
  }

  const checkIfAtBottom = () => {
    if (!scrollRef.current) return false
    
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight
    const atBottom = distanceFromBottom < threshold
    
    setIsAtBottom(atBottom)
    setShowButton(!atBottom)
    
    return atBottom
  }

  useEffect(() => {
    const element = scrollRef.current
    if (!element) return

    const handleScroll = () => {
      checkIfAtBottom()
    }

    element.addEventListener('scroll', handleScroll, { passive: true })
    
    // Check initial state
    checkIfAtBottom()

    return () => {
      element.removeEventListener('scroll', handleScroll)
    }
  }, [scrollRef, threshold])

  // Auto-scroll on new messages if at bottom
  useEffect(() => {
    if (!scrollRef.current) return

    const currentScrollHeight = scrollRef.current.scrollHeight
    const hasNewContent = currentScrollHeight > prevScrollHeight.current

    if (hasNewContent) {
      if (isAtBottom) {
        // Auto-scroll if already at bottom
        scrollToBottom('smooth')
      } else {
        // Show button if not at bottom
        onNewMessage?.()
      }
    }

    prevScrollHeight.current = currentScrollHeight
  }, [scrollRef.current?.scrollHeight, isAtBottom, onNewMessage])

  return {
    showButton,
    isAtBottom,
    scrollToBottom,
    checkIfAtBottom,
  }
}
